import { useState, useEffect, useCallback } from "react";
import { load } from "@cashfreepayments/cashfree-js";
import Layout from "../../layout/builders/Layout";
import useAuthStore from "../../store/useAuthStore";
import { axiosInstancev1 } from "../../lib/axiosInstance";
import toast from "react-hot-toast";

// ─── Constants ─────────────────────────────────────────────────────────────
const FREE_LIMIT = 5;

// ─── Sub-components ────────────────────────────────────────────────────────
const Badge = ({ children, color = "bg-green-100 text-green-700" }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${color}`}>
    {children}
  </span>
);

const SmallLabel = ({ children }) => (
  <label className="block text-[11px] font-semibold text-gray-500 mb-1 uppercase tracking-wide">
    {children}
  </label>
);

const SmallInput = ({ className = "/", ...props }) => (
  <input
    className={`w-full border border-gray-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white ${className}`}
    {...props}
  />
);

const EyeIcon = ({ open, onClick }) => (
  <button type="button" onClick={onClick} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
    {open ? (
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
    ) : (
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
    )}
  </button>
);

// ─── Pricing Banner ─────────────────────────────────────────────────────────
const PricingInfo = ({ pricing, currentCount, purchasedSlots }) => {
  const totalLimit = FREE_LIMIT + purchasedSlots;
  if (currentCount < totalLimit) return null;

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 flex items-center gap-3">
      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 shrink-0">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
      </div>
      <p className="text-[11px] text-amber-700 leading-relaxed">
        You have reached your limit of <strong>{totalLimit}</strong> incharges. 
        Please purchase additional slots to add more team members.
      </p>
    </div>
  );
};

// ─── Add / Edit Modal ────────────────────────────────────────────────────────
const InchargeModal = ({ open, onClose, onSave, onPurchase, editData, projects, pricing, currentCount, purchasedSlots }) => {
  const [form, setForm] = useState({ name: "", email: "", password: "", assigned_projects: [], permissions: [] });
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const totalLimit = FREE_LIMIT + purchasedSlots;
  const isLimitReached = !editData && currentCount >= totalLimit;

  useEffect(() => {
    if (editData) {
      setForm({
        name: editData.name || "",
        email: editData.email || "",
        password: "",
        assigned_projects: editData.assigned_projects || [],
        permissions: editData.permissions || [],
      });
      setSelectedPlan(null);
    } else {
      setForm({ name: "", email: "", password: "", assigned_projects: [], permissions: ["Projects", "ERP"] });
      setSelectedPlan(null);
    }
  }, [editData, open]);

  const handleProjectToggle = (projectId) => {
    setForm((prev) => {
      const ids = prev.assigned_projects.includes(projectId)
        ? prev.assigned_projects.filter((id) => id !== projectId)
        : [...prev.assigned_projects, projectId];
      return { ...prev, assigned_projects: ids };
    });
  };

  const handlePermissionToggle = (tab) => {
    setForm((prev) => {
      const perms = prev.permissions.includes(tab)
        ? prev.permissions.filter((p) => p !== tab)
        : [...prev.permissions, tab];
      return { ...prev, permissions: perms };
    });
  };

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLimitReached) {
      if (!selectedPlan) return toast.error("Please select a plan to continue");
      await onPurchase(selectedPlan.user_count);
      return;
    }
    if (!form.email) return toast.error("Email is required");
    if (!editData && !form.password) return toast.error("Password is required");
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  const paidPlans = pricing.filter(p => p.user_count > 5);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="bg-gray-50 px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-bold text-gray-800 uppercase tracking-tight">
            {isLimitReached ? "Upgrade Incharge Limit" : editData ? "Edit Incharge Profile" : "Create New Incharge"}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div className="p-5">
          {isLimitReached ? (
            <div className="space-y-4">
              <div className="text-center space-y-2">
                <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>
                </div>
                <h4 className="text-lg font-bold text-gray-900">Choose a Package</h4>
                <p className="text-xs text-gray-500">Select a slot package to add more Project Incharges.</p>
              </div>

              <div className="space-y-3">
                {paidPlans.map((plan) => (
                  <div 
                    key={plan.user_count}
                    onClick={() => setSelectedPlan(plan)}
                    className={`cursor-pointer p-4 rounded-xl border-2 transition-all flex items-center justify-between ${
                      selectedPlan?.user_count === plan.user_count 
                      ? "border-blue-500 bg-blue-50" 
                      : "border-gray-100 hover:border-gray-200 bg-white"
                    }`}
                  >
                    <div>
                      <p className="text-sm font-bold text-gray-800">
                        {plan.user_count === 6 ? "1 User Slot" : plan.user_count === 8 ? "3 User Slots" : "5 User Slots"}
                      </p>
                      <p className="text-[10px] text-gray-500">Additional capacity for your team</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-blue-600">₹{parseFloat(plan.amount).toLocaleString()}</p>
                      <p className="text-[9px] text-gray-400">One-time payment</p>
                    </div>
                  </div>
                ))}
              </div>

              <button 
                onClick={handleSubmit}
                disabled={!selectedPlan || saving}
                className="w-full bg-blue-600 text-white rounded-xl py-3 text-sm font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 disabled:opacity-50 mt-2"
              >
                {saving ? "Processing..." : `Pay ₹${selectedPlan ? parseFloat(selectedPlan.amount).toLocaleString() : '0'} & Continue`}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <SmallLabel>Full Name</SmallLabel>
                <SmallInput
                  type="text"
                  placeholder="e.g. Ramesh Kumar"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                />
              </div>

              <div>
                <SmallLabel>Email Address *</SmallLabel>
                <SmallInput
                  type="email"
                  placeholder="incharge@example.com"
                  value={form.email}
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  required
                />
              </div>

              <div>
                <SmallLabel>{editData ? "Reset Password" : "Secure Password *"}</SmallLabel>
                <div className="relative">
                  <SmallInput
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={form.password}
                    onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                  />
                  <EyeIcon open={showPassword} onClick={() => setShowPassword(!showPassword)} />
                </div>
              </div>

              <div>
                <SmallLabel>Assigned Projects</SmallLabel>
                <div className="max-h-24 overflow-y-auto border border-gray-100 rounded-xl p-2 space-y-1 bg-gray-50/50">
                  {projects.length === 0 ? (
                    <p className="text-[10px] text-gray-400 text-center py-2 italic">No projects available</p>
                  ) : (
                    projects.map((proj) => (
                      <label key={proj.id} className="flex items-center gap-2 cursor-pointer hover:bg-white rounded px-2 py-1 transition-colors">
                        <input
                          type="checkbox"
                          checked={form.assigned_projects.includes(proj.id)}
                          onChange={() => handleProjectToggle(proj.id)}
                          className="accent-blue-500 w-3 h-3"
                        />
                        <span className="text-[11px] text-gray-700 truncate">{proj.project_name}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 bg-gray-100 text-gray-600 rounded-xl py-2.5 text-xs font-bold hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-blue-600 text-white rounded-xl py-2.5 text-xs font-bold hover:bg-blue-700 transition-all shadow-md shadow-blue-100 disabled:opacity-50"
                >
                  {saving ? "Saving..." : editData ? "Update Account" : "Create Account"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Incharge Card ───────────────────────────────────────────────────────────
const InchargeCard = ({ incharge, projects, onEdit, onDelete }) => {
  const [deleting, setDeleting] = useState(false);

  const assignedNames = (incharge.assigned_projects || [])
    .map((id) => projects.find((p) => p.id === id)?.project_name)
    .filter(Boolean);

  const handleDelete = async () => {
    if (!window.confirm("Remove this incharge account?")) return;
    setDeleting(true);
    await onDelete(incharge.id);
    setDeleting(false);
  };

  const initials = (incharge.name || incharge.email || "?")
    .charAt(0)
    .toUpperCase();

  return (
    <div className="bg-white border border-gray-200 rounded-xl px-5 py-4 flex items-center gap-4 shadow-sm hover:shadow-md transition-shadow">
      {/* Avatar */}
      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-sm font-bold text-gray-600 shrink-0">
        {initials}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-800 truncate">{incharge.name || "—"}</p>
        <p className="text-xs text-gray-500 truncate">{incharge.email}</p>

        {/* Assigned Projects */}
        {assignedNames.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            <span className="text-[10px] text-gray-400 mr-1">Projects:</span>
            {assignedNames.map((name, idx) => (
              <Badge key={idx} color="bg-blue-50 text-blue-600">{name}</Badge>
            ))}
          </div>
        )}

      </div>

      {/* Actions */}
      <div className="flex gap-2 shrink-0">
        <button
          onClick={() => onEdit(incharge)}
          className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors font-medium"
        >
          Edit
        </button>
        <button
          onClick={handleDelete}
          disabled={deleting}
          className="text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50 font-medium"
        >
          {deleting ? "..." : "Remove"}
        </button>
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────
const ProjectIncharge = () => {
  const { user, token } = useAuthStore();

  const [incharges, setIncharges] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [purchasedSlots, setPurchasedSlots] = useState(0);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  const builderId = user?.builderId || user?.builder_id || user?.id;

  const [cashfree, setCashfree] = useState(null);

  useEffect(() => {
    const initializeCashfree = async () => {
      try {
        const mode = process.env.REACT_APP_CASHFREE_MODE || "production";
        const cf = await load({ mode });
        setCashfree(cf);
      } catch (error) {
        console.error("Failed to load Cashfree SDK:", error);
      }
    };
    initializeCashfree();
  }, []);

  const fetchData = useCallback(async () => {
    if (!token || !builderId) return;
    setLoading(true);
    try {
      const [inchargeRes, projectsRes] = await Promise.all([
        axiosInstancev1.get("/builder/incharges", {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axiosInstancev1.get(`/projects?builderId=${builderId}&status=active&limit=100&page=1`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (inchargeRes.data.success) {
        setIncharges(inchargeRes.data.incharges || []);
        setPricing(inchargeRes.data.pricing || []);
        setPurchasedSlots(inchargeRes.data.purchasedSlots || 0);
      }
      if (projectsRes.data.success) {
        setProjects(projectsRes.data.projects || []);
      }
    } catch (error) {
      console.error("Error fetching incharge data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [token, builderId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenAdd = () => {
    setEditData(null);
    setModalOpen(true);
  };

  const handleEdit = (incharge) => {
    setEditData(incharge);
    setModalOpen(true);
  };

  const handleSave = async (form) => {
    try {
      if (editData) {
        // Update
        const payload = { 
          name: form.name, 
          email: form.email, 
          assigned_projects: form.assigned_projects,
          permissions: form.permissions
        };
        if (form.password) payload.password = form.password;
        await axiosInstancev1.put(`/builder/incharges/${editData.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Incharge updated");
      } else {
        // Create
        const payload = {
          ...form,
          assignedProjects: form.assigned_projects // Controller expects camelCase for consistency with projects
        };
        await axiosInstancev1.post("/builder/incharges", payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Incharge created successfully");
      }
      setModalOpen(false);
      fetchData();
    } catch (error) {
      const msg = error?.response?.data?.message || "Failed to save incharge";
      toast.error(msg);
    }
  };

  const handlePurchase = async (user_count) => {
    // Map user_count to actual slot counts (6->1, 8->3, 10->5)
    const slotMap = { 6: 1, 8: 3, 10: 5 };
    const slots = slotMap[user_count];
    if (!slots) return;

    // Find the plan to get the amount
    const plan = pricing.find(p => p.user_count === user_count);
    if (!plan) return;

    if (!cashfree) {
      toast.error("Payment system not ready. Please refresh.");
      return;
    }

    try {
      // 1. Create order on backend
      const { data } = await axiosInstancev1.post("/builder/incharges/create-order", {
        amount: plan.amount,
        slotsToAdd: slots
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!data.success) throw new Error(data.message);

      // 2. Open Cashfree Checkout
      const checkoutOptions = {
        paymentSessionId: data.data.paymentSessionId,
        redirectTarget: "_modal", // Opens in a modal/popup
      };

      cashfree.checkout(checkoutOptions).then(async (result) => {
        if (result.error) {
          toast.error(result.error.message);
          return;
        }

        if (result.redirect) {
          // This only happens if redirectTarget is _self
          return;
        }

        // 3. Verify payment on backend
        const verifyRes = await axiosInstancev1.post("/builder/incharges/verify-payment", {
          orderId: data.data.orderId
        }, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (verifyRes.data.success) {
          toast.success(`Successfully purchased ${slots} ${slots === 1 ? 'slot' : 'slots'}!`);
          fetchData();
        } else {
          toast.error("Payment verification failed. Please contact support.");
        }
      });

    } catch (error) {
      const msg = error?.response?.data?.message || error.message || "Payment failed";
      toast.error(msg);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axiosInstancev1.delete(`/builder/incharges/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Incharge removed");
      fetchData();
    } catch (error) {
      toast.error("Failed to remove incharge");
    }
  };

  return (
    <Layout>
      <div className="px-6 py-4">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-1">
          <h1 className="text-2xl font-bold text-gray-900">Project Incharge</h1>
          <button
            onClick={handleOpenAdd}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors flex items-center gap-1.5"
          >
            + Add Incharge
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Create sub-accounts for site supervisors with restricted project access. First {FREE_LIMIT} users are free.
        </p>

        <hr className="border-gray-200 mb-6" />

        {/* Pricing info banner when adding beyond free limit */}
        <PricingInfo pricing={pricing} currentCount={incharges.length} purchasedSlots={purchasedSlots} />

        {/* Stats row */}
        <div className="flex gap-4 mb-6">
          <div className="bg-white border border-gray-100 rounded-2xl px-6 py-5 text-center shadow-sm min-w-[130px] border-b-4 border-b-blue-500">
            <p className="text-3xl font-bold text-gray-900">{incharges.length}</p>
            <p className="text-[10px] text-gray-400 uppercase font-bold tracking-wider mt-1">Total Team</p>
          </div>
          <div className="bg-white border border-gray-100 rounded-2xl px-6 py-5 text-center shadow-sm min-w-[130px] border-b-4 border-b-green-500">
            <p className="text-3xl font-bold text-green-600 font-mono">{Math.min(incharges.length, FREE_LIMIT)}</p>
            <p className="text-[10px] text-gray-400 uppercase font-bold tracking-wider mt-1">Free Slots</p>
          </div>
          <div className="bg-white border border-gray-100 rounded-2xl px-6 py-5 text-center shadow-sm min-w-[130px] border-b-4 border-b-amber-500">
            <p className="text-3xl font-bold text-amber-500 font-mono">{purchasedSlots}</p>
            <p className="text-[10px] text-gray-400 uppercase font-bold tracking-wider mt-1">Paid Slots</p>
          </div>
        </div>

        {/* Pricing Table (informational) */}
        {pricing.filter((p) => p.user_count > FREE_LIMIT).length > 0 && (
          <details className="mb-6 bg-gray-50/50 border border-gray-200 rounded-2xl overflow-hidden group">
            <summary className="cursor-pointer px-5 py-3 text-xs font-bold text-gray-500 hover:bg-gray-100 transition-colors uppercase tracking-widest list-none flex items-center justify-between">
              Upgrade Plans Available
              <svg className="w-4 h-4 transition-transform group-open:rotate-180" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
            </summary>
            <div className="p-4 border-t border-gray-100">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {pricing
                  .filter((p) => p.user_count > FREE_LIMIT)
                  .map((p) => (
                    <div key={p.user_count} className="flex justify-between items-center text-xs bg-white border border-gray-100 rounded-xl px-4 py-3 shadow-sm">
                      <span className="font-bold text-gray-700">
                        {p.user_count === 6 ? "1 User" : p.user_count === 8 ? "3 Users" : "5 Users"}
                      </span>
                      <span className="font-black text-blue-600">₹{parseFloat(p.amount).toLocaleString()}</span>
                    </div>
                  ))}
              </div>
            </div>
          </details>
        )}

        {/* List */}
        {loading ? (
          <div className="text-center py-16">
            <div className="inline-block h-7 w-7 animate-spin rounded-full border-2 border-gray-300 border-t-gray-700" />
            <p className="text-sm text-gray-400 mt-2">Loading...</p>
          </div>
        ) : incharges.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-gray-200">
            <p className="text-base font-semibold text-gray-600">No incharges yet</p>
            <p className="text-sm text-gray-400 mt-1">Create your first project incharge to get started.</p>
            <button
              onClick={handleOpenAdd}
              className="mt-4 bg-blue-600 text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors"
            >
              + Add Incharge
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {incharges.map((incharge) => (
              <InchargeCard
                key={incharge.id}
                incharge={incharge}
                projects={projects}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      <InchargeModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        onPurchase={handlePurchase}
        editData={editData}
        projects={projects}
        pricing={pricing}
        currentCount={incharges.length}
        purchasedSlots={purchasedSlots}
      />
    </Layout>
  );
};

export default ProjectIncharge;
